import { useEffect } from "react";

import { CurrencyType, DateRangeValue, ReportFilters } from "store/types";
import { camelCaseToTitle } from "utils";

import { inlineMeasureAttributes } from "../Sections/Text-section/use-inline-measure";

export const useInlineMeasureTooltip = () => {
  const tooltipRoot = document.body;

  const listenMouseOver = (e: MouseEvent) => {
    const element = e.target as HTMLElement;

    if (!element) return;

    const inlineMeasure = identifyInlineMeasure(element);
    if (!inlineMeasure) return;

    const tooltip = document.createElement("div");
    tooltip.className = "richText-inline-measure-tooltip";
    tooltip.textContent = formatMeasureTooltip(JSON.parse(inlineMeasure.measureDataSettings));

    // Append tooltip temporarily to measure its dimensions
    tooltip.style.visibility = "hidden";
    tooltip.style.position = "fixed";
    tooltipRoot.appendChild(tooltip);

    const rect = element.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate available space in each direction
    const spaceAbove = rect.top;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;

    // Determine best position based on available space
    const tooltipWidth = tooltipRect.width;
    const tooltipHeight = tooltipRect.height;
    const spacing = 8; // Spacing between anchor and tooltip

    let top = 0;
    let left = 0;

    // Priority: bottom > top > right > left (but choose based on available space)
    if (spaceBelow >= tooltipHeight + spacing) {
      // Position below
      top = rect.bottom + spacing;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    } else if (spaceAbove >= tooltipHeight + spacing) {
      // Position above
      top = rect.top - tooltipHeight - spacing;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    } else if (spaceRight >= tooltipWidth + spacing) {
      // Position to the right
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + spacing;
    } else if (spaceLeft >= tooltipWidth + spacing) {
      // Position to the left
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - spacing;
    } else {
      // Fallback: position below, but adjust to fit viewport
      top = rect.bottom + spacing;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    }

    // Ensure tooltip stays within viewport bounds
    top = Math.max(0, Math.min(top, viewportHeight - tooltipHeight));
    left = Math.max(0, Math.min(left, viewportWidth - tooltipWidth));

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
    tooltip.style.visibility = "visible";
  };

  const listenMouseOut = (e: MouseEvent) => {
    const element = e.target as HTMLElement;

    if (!element) return;

    // const inlineMeasure = identifyInlineMeasure(element);
    // if (!inlineMeasure) return;

    const tooltip = document.body.querySelector(".richText-inline-measure-tooltip");

    if (!tooltip) return;

    tooltip.classList.add("richText-inline-measure-tooltip__fade-out");
    setTimeout(() => {
      tooltip.remove();
    }, 300);
  };

  useEffect(() => {
    document.addEventListener("mouseover", listenMouseOver);
    document.addEventListener("mouseout", listenMouseOut);

    return () => {
      document.removeEventListener("mouseover", listenMouseOver);
      document.removeEventListener("mouseout", listenMouseOut);
    };
  }, []);
};

const identifyInlineMeasure = (element: HTMLElement) => {
  const measureIdAttr = element.attributes.getNamedItem(inlineMeasureAttributes.measureId);
  const measureDataSettingsAttr = element.attributes.getNamedItem(inlineMeasureAttributes.measureDataSettings);

  if (!measureIdAttr || !measureDataSettingsAttr) return null;
  return { measureId: measureIdAttr.value, measureDataSettings: measureDataSettingsAttr.value };
};

// Helper function to format tooltip content from measure settings
const formatMeasureTooltip = (settings: {
  selectedOptions: { type: string; option: string }[];
  queries: {
    dateRange: DateRangeValue;
    currency: CurrencyType;
    filters: ReportFilters;
    exchangeMethod: "toDate" | "realDate";
  };
}): string => {
  const { selectedOptions, queries } = settings;

  // Build list of selected measures/characteristics
  const measureNames = selectedOptions.map((opt) => opt.option).join(", ");

  // Build query info
  const queryParts: string[] = [];
  if (queries?.currency) {
    queryParts.push(`Currency: ${queries.currency}`);
  }
  if (queries?.dateRange) {
    const dateRange = queries.dateRange;
    if (typeof dateRange === "object" && dateRange.value) {
      queryParts.push(`Period: ${dateRange.value}`);
    }
  }

  if (queries?.exchangeMethod) {
    queryParts.push(`Hedging method: ${queries.exchangeMethod}`);
  }

  if (Object.keys(queries?.filters || {}).length) {
    const filterStrings = Object.keys(queries.filters)
      .filter((key) => queries.filters[key] && queries.filters[key].length > 0)
      .map((key) => {
        const formattedKey = camelCaseToTitle(key);
        return `${formattedKey}: ${queries.filters[key].join(", ")}`;
      });

    if (filterStrings.length > 0) {
      queryParts.push(`Filters: ${filterStrings.join(", ")}`);
    }
  }

  const tooltipContent = [measureNames, ...queryParts].filter(Boolean).join("\n");
  return tooltipContent || "Inline measure";
};
